import type { PlacementTargetingProps, PlacementTargetingRequestBody } from "./types";

/**
 * Builds the `targeting` request body shared by the placement create (POST) and update (PATCH)
 * fetchers from the form's `PlacementTargetingProps`.
 *
 * Always returns a concrete request body (never `undefined`) -- this helper cannot tell the
 * difference between "the caller has no targeting to send" and "the caller doesn't want to
 * change existing targeting," so it treats `null`/`undefined` input the same way a fully-cleared
 * targeting object would be treated: no CSS selector, so `null`. Callers that need the "leave
 * unchanged" behavior (i.e. the PATCH fetcher) must check for `undefined` themselves *before*
 * calling this helper, and omit the `targeting` key from the request entirely in that case.
 *
 * @param targeting the form's targeting values, or `undefined`/`null` when there's nothing to target
 * @return the snake_case request body, or `null` when there's no CSS selector to target
 */
export const buildPlacementTargetingBody = (
  targeting: PlacementTargetingProps | null | undefined
): PlacementTargetingRequestBody | null => {
  if (!targeting?.cssSelector) {
    return null;
  }
  return {
    css_selector: targeting.cssSelector,
    insertion_position: targeting.insertionPosition,
    url: targeting.url || null,
    url_match_type: targeting.urlMatchType || null,
  };
};
